import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

/** Icon data from `@hugeicons/core-free-icons` (e.g. `Home01Icon`). */
export type IconData = IconSvgElement;

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'ref' | 'children'> {
  /** Hugeicons icon data, e.g. `import { Home01Icon } from '@hugeicons/core-free-icons'`. */
  icon: IconData;
  /**
   * Accessible name. Omit for decorative icons (the default): they are hidden from assistive tech with
   * `aria-hidden`. When set, the icon is exposed as `role="img"` with this label.
   */
  label?: string;
  /** Stroke width (1.75 by default: a touch heavier than Hugeicons' 1.5 so small icons stay legible). */
  strokeWidth?: number;
}

/**
 * Single entry point for icons (Hugeicons free set). Size it with Tailwind (`size-4`, `size-5`) or let the
 * parent component size it (`[&_svg]:size-4`); unsized icons render at 24px. Decorative by default.
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { icon, label, strokeWidth = 1.75, className, ...rest },
  ref,
) {
  const a11y = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': true as const, focusable: 'false' as const };
  return (
    <HugeiconsIcon
      ref={ref}
      icon={icon}
      strokeWidth={strokeWidth}
      className={cn(className)}
      {...a11y}
      {...rest}
    />
  );
});
